import { useInfiniteQuery } from "@tanstack/react-query";
import { useAxios } from "../../../api/axios";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import useProperty from "../../context/useProperty";

export const usePreList = ({ type }: { type: string }) => {
  const axios = useAxios();
  const { t } = useTranslation();
  const { coords } = useProperty();
  const [searchParams] = useSearchParams();

  // Get search parameters
  const searchTextValue = searchParams.get("searchTxtPre") || "";
  const sortValue = searchParams.get("sort") || "DESC";

  const fetchPreList = async ({ pageParam = 1 }: { pageParam: number }) => {
    const urlKey = t("pre-list-api");
    
    const mapBounds = coords ? {
      topLeftLng: coords.topLeftLng,
      topLeftLat: coords.topLeftLat,
      bottomRightLat: coords.bottomRightLat,
      bottomRightLng: coords.bottomRightLng
    } : {
      topLeftLng: -118.52031247275694,
      topLeftLat: 34.26728827119982,
      bottomRightLat: 33.78743855506191,
      bottomRightLng: -117.75531165205248
    };

    const response = await axios.post(urlKey, {
      page: pageParam,
      limit: 10,
      mapBounds,
      sortOrder: sortValue,
      // NOTE: pre listing API expects 'searchTxtPre' (keep other APIs unchanged)
      ...(searchTextValue && { searchTxtPre: searchTextValue })
    });

    const resp = response?.data || {};
    const data = Array.isArray(resp?.data?.data) ? resp.data.data : [];
    const pagination = resp?.data?.pagination || {};
    const page = Number(pagination?.page) || pageParam;
    const limit = Number(pagination?.limit) || 10;
    const total = Number(pagination?.total) || 0;
    const totalPages = Number(pagination?.totalPages) || (total && limit ? Math.ceil(total / limit) : 0);
    return {
      data,
      currentPage: page,
      totalCount: total,
      totalPages
    };
  };

  const {
    data: arrPreList,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
    isPlaceholderData
  } = useInfiniteQuery({
    queryKey: [
      `pre-list`,
      coords,
      searchTextValue,
      sortValue,
      type
    ],
    queryFn: fetchPreList,
    initialPageParam: 1,
    placeholderData: (oldData) => oldData,
    getNextPageParam: (lastPage: any, allPages: any) => {
      const totalPages = Number(lastPage?.totalPages) || 0;
      const currentPage = Number(lastPage?.currentPage) || 0;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled: type === "agent",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  return {
    arrPreList,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
    isPlaceholderData
  };
};
